import { Link } from "react-router-dom";
import { Play, Layers, Lock } from "lucide-react";
import { mediaSrc, prefetchMedia } from "../lib/api";
import { SaveButton } from "./SaveButton";

export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const thumbOf = (m) => mediaSrc(m.thumb_url || (m.media_type === "image" ? m.url : null));

export const ArchiveTile = ({ m }) => (
  <Link to={`/media/${m.id}`} data-testid={`archive-tile-${m.id}`} onMouseEnter={() => prefetchMedia(m.id)} onTouchStart={() => prefetchMedia(m.id)}
    className="group relative block aspect-square overflow-hidden rounded-lg bg-[#111] ring-1 ring-[rgb(var(--kv-glow)/calc(0.25*var(--kv-glow-i)))] hover:ring-[rgb(var(--kv-glow)/calc(0.8*var(--kv-glow-i)))] transition-shadow">
    {thumbOf(m) ? <img src={thumbOf(m)} alt={m.title || ""} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      : <div className="w-full h-full flex items-center justify-center text-[#52525B]"><Play size={22} /></div>}
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    <SaveButton item={m} />
    <span className="absolute bottom-1.5 left-2 right-2 text-[11px] text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">{m.title}</span>
    <span className="absolute top-1.5 right-1.5 flex gap-1">
      {m.media_type === "video" && <Play size={13} className="text-white drop-shadow" fill="white" />}
      {m.is_set && <Layers size={13} className="text-white drop-shadow" />}
      {m.is_private && <Lock size={13} className="text-[#F1A817] drop-shadow" />}
    </span>
  </Link>
);

export const ArchiveRow = ({ m }) => (
  <Link to={`/media/${m.id}`} data-testid={`archive-row-${m.id}`} onMouseEnter={() => prefetchMedia(m.id)} onTouchStart={() => prefetchMedia(m.id)}
    className="flex gap-4 items-center p-3 rounded-xl border border-white/[0.06] bg-[#0D0E12] hover:border-[rgb(var(--kv-glow)/0.5)] hover:bg-white/[0.03] transition-colors">
    <div className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-lg overflow-hidden bg-[#111]">
      {thumbOf(m) ? <img src={thumbOf(m)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center text-[#52525B]"><Play size={20} /></div>}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-white font-semibold text-sm truncate">{m.title || "Untitled"}</p>
      {m.description && <p className="text-[#A1A1AA] text-xs mt-1 line-clamp-2">{m.description}</p>}
      <p className="font-mono text-[10px] text-[#71717A] mt-2 flex flex-wrap gap-x-3 gap-y-1 uppercase tracking-wider">
        <span>{new Date(m.created_at).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" })}</span>
        <span>@{m.author_username}</span>
        <span>{m.media_type}{m.is_set ? ` · ${m.asset_count} items` : ""}</span>
        <span>{m.likes_count || 0} likes</span>
        {m.tags?.slice(0, 3).map((t) => <span key={t} className="text-[#00E5FF]">#{t}</span>)}
      </p>
    </div>
    <SaveButton item={m} variant="inline" />
  </Link>
);

export const Timeline = ({ months, sel, onPick }) => {
  const chron = [...months].reverse();
  const max = Math.max(1, ...chron.map((x) => x.count));
  if (!chron.length) return null;
  return (
    <div data-testid="archive-timeline" className="mb-6 rounded-2xl border border-white/[0.06] bg-[#0D0E12] p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-3">Archive growth</p>
      <div className="flex items-end gap-[3px] h-24 overflow-x-auto pb-1">
        {chron.map((x) => {
          const on = sel.y === x.year && sel.m === x.month;
          return (
            <button key={`${x.year}-${x.month}`} data-testid={`timeline-bar-${x.year}-${x.month}`} onClick={() => onPick(x.year, x.month)}
              title={`${MONTHS[x.month - 1]} ${x.year}: ${x.count}`}
              style={{ height: `${Math.max(6, (x.count / max) * 100)}%` }}
              className={`min-w-[10px] flex-1 max-w-[28px] rounded-t transition-colors ${on ? "bg-[rgb(var(--kv-glow))]" : "bg-[rgb(var(--kv-glow)/0.35)] hover:bg-[rgb(var(--kv-glow)/0.7)]"}`} />
          );
        })}
      </div>
      <div className="flex justify-between font-mono text-[10px] text-[#52525B] mt-1">
        <span>{MONTHS[chron[0].month - 1].slice(0, 3)} {chron[0].year}</span>
        <span>{MONTHS[chron[chron.length - 1].month - 1].slice(0, 3)} {chron[chron.length - 1].year}</span>
      </div>
    </div>
  );
};

export const MonthNav = ({ months, sel, onPick }) => {
  const years = [...new Set(months.map((x) => x.year))];
  const total = months.reduce((a, x) => a + x.count, 0);
  const item = (on) => `w-full flex justify-between items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${on ? "bg-[rgb(var(--kv-glow)/0.15)] text-white" : "text-[#A1A1AA] hover:text-white hover:bg-white/[0.04]"}`;
  return (
    <nav data-testid="archive-month-nav" className="space-y-3">
      <button data-testid="archive-all" onClick={() => onPick(null, null)} className={item(!sel.y)}><span>All time</span><span className="font-mono text-[11px]">{total}</span></button>
      {years.map((y) => (
        <div key={y}>
          <button data-testid={`archive-year-${y}`} onClick={() => onPick(y, null)} className={`${item(sel.y === y && !sel.m)} font-heading`}>
            <span>{y}</span><span className="font-mono text-[11px]">{months.filter((x) => x.year === y).reduce((a, x) => a + x.count, 0)}</span>
          </button>
          <div className="ml-3 border-l border-white/[0.06] pl-1 mt-1 space-y-0.5">
            {months.filter((x) => x.year === y).map((x) => (
              <button key={x.month} data-testid={`archive-month-${y}-${x.month}`} onClick={() => onPick(y, x.month)} className={item(sel.y === y && sel.m === x.month)}>
                <span>{MONTHS[x.month - 1]}</span><span className="font-mono text-[11px]">{x.count}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
};
